import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import {
  ALLOWED_STAGE_TRANSITIONS,
  APPLICATION_STAGE_LABELS,
  advanceStage,
  type ApplicationRead,
  type ApplicationStage,
} from '@/features/recruitment/api'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

/**
 * The one door a stage moves through. Shared between the pipeline table's inline action
 * and `ApplicationDetail`, so the forward-only graph and the rejection-reason requirement
 * are enforced in exactly one place on the frontend, matching the single server route
 * behind both -- see `advanceStage`.
 */
export function StageChangeDialog({
  open,
  application,
  onClose,
}: {
  open: boolean
  application?: ApplicationRead
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const options = application ? ALLOWED_STAGE_TRANSITIONS[application.stage] : []
  const [target, setTarget] = useState<ApplicationStage | ''>('')
  const [reason, setReason] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const close = () => {
    setTarget('')
    setReason('')
    setFormError(null)
    onClose()
  }

  const move = useMutation({
    mutationFn: () => {
      if (!application || !target) throw new Error('No stage chosen')
      return advanceStage(application.id, target, target === 'rejected' ? reason : undefined)
    },
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['applications'] })
      queryClient.setQueryData(['application', updated.id], updated)
      toast.success(`Moved to ${APPLICATION_STAGE_LABELS[updated.stage]}.`)
      close()
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError ? error.message : 'Could not reach the server. Nothing was changed.',
      )
    },
  })

  const ready = Boolean(target) && (target !== 'rejected' || reason.trim().length > 0)

  return (
    <Modal
      open={open}
      onClose={close}
      title={application ? `Move ${application.candidate_name ?? 'this application'}` : 'Move application'}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => move.mutate()} disabled={!ready} loading={move.isPending}>
            Move
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <Alert tone="error">{formError}</Alert>}

        {options.length === 0 ? (
          <Alert tone="info">
            {application ? APPLICATION_STAGE_LABELS[application.stage] : 'This stage'} is a final
            stage. It has no further move.
          </Alert>
        ) : (
          <>
            <Field label="New stage">
              {(props) => (
                <select
                  {...props}
                  className={SELECT_CLASS}
                  value={target}
                  onChange={(event) => setTarget(event.target.value as ApplicationStage)}
                >
                  <option value="">Choose a stage…</option>
                  {options.map((stage) => (
                    <option key={stage} value={stage}>
                      {APPLICATION_STAGE_LABELS[stage]}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            {target === 'rejected' && (
              <Field label="Rejection reason" hint="Required when rejecting.">
                {(props) => (
                  <Input {...props} value={reason} onChange={(event) => setReason(event.target.value)} />
                )}
              </Field>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
